import React from 'react'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Entity } from 'shared-libs/models/entity'

export const NUM_SEARCH_RESULTS_TO_SHOW = 20
export const DEFAULT_ENTITY_TYPE_NAME = 'document'
export const DEFAULT_SEARCH_ORDER = [
  {
    path: 'creationDate',
    type: 'descending',
  },
]

export const DEFAULT_MOBILE_WEB_SCHEMA_PATHS = {
  item: 'uiSchema.mobileWeb.entitySearchItem',
  help: 'uiSchema.mobileWeb.entitySearchHelpSection',
  inlinePreview: 'uiSchema.mobileWeb.entitySearchItem',
  emptyResults: 'uiSchema.mobileWeb.entitySearchEmptyResults',
}

/**
 * @uiComponent
 */
export interface IMobileEntitySearchFieldProps extends IBaseProps {
  errors?: string[]
  isEditable?: boolean
  isDisabled?: boolean
  isReadOnly?: boolean
  isHorizontalLayout?: boolean
  isClearable?: boolean
  label?: string
  onChange: (value: any) => void
  onEntityChange?: (value: { entityId: string | undefined; entity: Entity | undefined }) => void
  value?: any
  entity?: any
  defaultValue?: any
  query?: any
  minQueryLength?: number
  placeholder?: string
  inlinePreviewSchemaPath?: any
  preloadResults?: boolean
  autoFocus?: boolean
  /**
   * 'auto' (default) - Search results will be fetched and shown as user types in the search field
   * 'submit' - Search results will only be fetched and shown when the user hits a submit button
   */
  searchMode?: 'auto' | 'submit'
  /**
   * (Auto mode only) When set, tapping a search result highlights it instead of committing
   * immediately. The user then commits the highlighted result via a pinned confirm button. Use for
   * flows that need an explicit confirmation step (e.g. binding the selection to a target spot).
   */
  confirmOnSelect?: boolean
  /**
   * (Auto mode only) Expression for the confirm button label, evaluated against the parent workflow
   * context (via frames) plus `selectedItem`/`item` (the highlighted entity's data) and `searchFilter`.
   * Only applies when confirmOnSelect is set. Example:
   * "'Add ' + selectedItem.displayName + ' to ' + _transient.spotName". Falls back to "Select <Entity>".
   */
  confirmButtonLabel?: string
  onSubmitSearch?: (payload: { hasResults: boolean; numberOfAttempts: number }) => void
  valueMappings?: any[]
  optionValuePath?: string
  /**
   * The path of the schema to use for rendering each search result.
   * Default is `uiSchema.mobileWeb.entitySearchItem`. If this path or the provided path
   * does not exist in the entity schema, it will fall back to a default item schema that shows the entity's display name.
   */
  itemSchemaPath?: string
  /**
   * The path of the schema to use when no results are returned from the search query.
   * Default is `uiSchema.mobileWeb.entitySearchEmptyResults`. If this path or the provided path
   * does not exist in the entity schema, it will fall back to a default empty results schema.
   */
  emptyResultsSchemaPath?: string
  /**
   * The path of the schema to use for rendering the help section in the search overlay. This section is
   * intended to provide search tips to the user before any search term is entered. Only applicable
   * in 'auto' mode when 'preloadResults' is false, as the help section cannot be shown if search results are displayed.
   * Default is `uiSchema.mobileWeb.entitySearchHelpSection`. If this path or the provided path does not exist in the entity schema,
   * a generic help section will be rendered.
   */
  helpSchemaPath?: string
  /**
   * A function to transform the search filter before it's used in the query. This can be used to implement features like silently adding wildcards
   * to the search filter for partial matching.
   * @param input The search filter input by the user
   * @returns The transformed search filter to be used in the query
   */
  transformSearchFilterFn?: (input: string) => string
  /**
   * A function to transform the search results returned from the query before they are rendered. This can be used to implement features
   * like advanced sorting, filtering, or grouping of results.
   * The function will receive the search results as well as the original search query for context.
   * @param results The search results returned from the query
   * @returns The transformed search results to be rendered
   */
  transformResultsFn?: (results: {entities: any[]; searchQuery: string}) => any[] | Promise<any[]>
  renderItem?: (props: {
    entity: Entity
    item: Entity
    selectedItem: Entity | undefined
    selectOption: () => void
  }) => React.ReactNode
  frames?: any
  /**
   * (Submit mode only) When set, adds an option to the results list to submit the user's typed value as a
   * raw string to this path instead of selecting an entity. This is useful for cases where a driver may not
   * be able to find a facility-owned entity in the system, but we don't want them to create a new entity.
   * Instead, we can capture their typed value and surface it up to facility staff for review.
   */
  rawQueryPath?: string
  /**
   * (Submit mode only) Label template for the raw submit option. Use '{searchFilter}' as a placeholder for the
   * user's typed value. Defaults to `Not listed? Use "{searchFilter}" instead`.
   * Only applies when rawQueryPath is set.
   */
  rawSubmitLabel?: string
  /**
   * Custom title for the search modal header. Defaults to "Find <EntityType>".
   */
  searchModalTitle?: string
  /**
   * Optional subtitle shown below the title in the search modal header.
   */
  searchModalSubtitle?: string
}

export interface IResolvedSchemaPaths {
  item?: string
  help?: string
  emptyResult?: string
  inlinePreview?: string
}

export interface ISelectionPayload {
  entity: Entity
  searchFilter: string
}
